/**
 * Hardening helpers for the persistent notification inbox.
 * Keeps KV load/save crash-safe and business-scoped.
 */

import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess';

export const LEGACY_PERSISTENT_NOTIFICATIONS_KEY = 'persistentNotifications';
export const NOTIFICATION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function persistentNotificationsStorageKey(businessId) {
  const id = String(businessId || '').trim();
  if (!id) return LEGACY_PERSISTENT_NOTIFICATIONS_KEY;
  return `persistentNotifications_${id}`;
}

export function isValidNotificationTimestamp(value) {
  if (!value) return false;
  const ms = Date.parse(value);
  return Number.isFinite(ms);
}

/**
 * Coerce a single inbox row into a safe shape. Returns null if unusable.
 * When `businessId` is passed and the row has no scope yet, tags it (save path).
 * Load path should sanitize without businessId first, then filter strictly.
 */
export function sanitizeNotification(raw, { businessId = null, now = Date.now() } = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const id = raw.id != null ? String(raw.id) : '';
  if (!id) return null;

  const createdAt = isValidNotificationTimestamp(raw.createdAt)
    ? raw.createdAt
    : new Date(now).toISOString();

  let data = {};
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    try {
      data = JSON.parse(JSON.stringify(raw.data));
    } catch {
      data = {};
    }
  }

  const existingScope =
    data.businessId != null && String(data.businessId).trim()
      ? String(data.businessId)
      : null;
  // Only tag unscoped rows when explicitly saving for a business — never invent
  // scope during legacy load (that retagged old-business alerts onto a new login).
  if (!existingScope && businessId) {
    data = { ...data, businessId: String(businessId) };
  } else if (existingScope) {
    data = { ...data, businessId: existingScope };
  }

  const category =
    typeof raw.category === 'string' && raw.category.trim()
      ? raw.category.trim()
      : 'GENERAL';

  const type =
    raw.type === 'success' || raw.type === 'error' || raw.type === 'warning' || raw.type === 'info'
      ? raw.type
      : 'info';

  return {
    id,
    title: raw.title != null ? String(raw.title) : '',
    message: raw.message != null ? String(raw.message) : '',
    type,
    category,
    persistent: raw.persistent !== false,
    data,
    read: Boolean(raw.read),
    createdAt,
    duration: Number.isFinite(Number(raw.duration)) ? Number(raw.duration) : 4500,
    priority: Number.isFinite(Number(raw.priority)) ? Number(raw.priority) : 6
  };
}

export function sanitizeNotificationList(list, options = {}) {
  if (!Array.isArray(list)) return [];
  const now = options.now ?? Date.now();
  const maxAge = options.maxAgeMs ?? NOTIFICATION_MAX_AGE_MS;
  const seenIds = new Set();
  const seenEventKeys = new Set();
  const out = [];

  for (const item of list) {
    const clean = sanitizeNotification(item, { ...options, now });
    if (!clean) continue;

    const createdMs = Date.parse(clean.createdAt);
    if (Number.isFinite(createdMs) && now - createdMs > maxAge) continue;

    if (seenIds.has(clean.id)) continue;
    seenIds.add(clean.id);

    const eventKey = clean.data?.eventKey != null ? String(clean.data.eventKey) : '';
    if (eventKey) {
      if (seenEventKeys.has(eventKey)) continue;
      seenEventKeys.add(eventKey);
    }

    out.push(clean);
  }

  return out;
}

/**
 * Merge incoming notifications into existing, deduping by id and eventKey.
 * When the same eventKey already exists, keep the existing row (no spam).
 */
export function mergeNotificationsRobust(existing, incoming) {
  const base = Array.isArray(existing) ? existing : [];
  if (!incoming?.length) return base;

  const seenIds = new Set(base.map((n) => n?.id).filter((id) => id != null).map(String));
  const seenEventKeys = new Set(
    base
      .map((n) => (n?.data?.eventKey != null ? String(n.data.eventKey) : ''))
      .filter(Boolean)
  );

  const merged = [...base];
  for (const item of incoming) {
    if (!item || item.id == null) continue;
    const id = String(item.id);
    if (seenIds.has(id)) continue;

    const eventKey = item.data?.eventKey != null ? String(item.data.eventKey) : '';
    if (eventKey && seenEventKeys.has(eventKey)) continue;

    seenIds.add(id);
    if (eventKey) seenEventKeys.add(eventKey);
    merged.push(item);
  }
  return merged;
}

export function filterNotificationsForBusiness(list, businessId) {
  const id = String(businessId || '').trim();
  if (!id) return Array.isArray(list) ? list : [];
  return (Array.isArray(list) ? list : []).filter((n) => {
    const scoped = n?.data?.businessId;
    // Strict: unscoped legacy must not leak across businesses on a shared PC
    if (scoped == null || scoped === '') return false;
    return String(scoped) === id;
  });
}

export function loadPersistentNotificationsFromKv(businessId) {
  const scopedKey = persistentNotificationsStorageKey(businessId);
  let stored = kvGet(scopedKey);
  let usedLegacy = false;

  if (
    (stored == null || (Array.isArray(stored) && stored.length === 0)) &&
    businessId &&
    scopedKey !== LEGACY_PERSISTENT_NOTIFICATIONS_KEY
  ) {
    const legacy = kvGet(LEGACY_PERSISTENT_NOTIFICATIONS_KEY);
    if (legacy != null) {
      stored = legacy;
      usedLegacy = true;
    }
  }

  if (stored == null) {
    return { notifications: [], usedLegacy: false, key: scopedKey };
  }

  let parsed;
  if (typeof stored === 'string') {
    if (
      stored === '[object Object]' ||
      stored === '"[object Object]"' ||
      stored.trim() === ''
    ) {
      kvRemove(scopedKey);
      if (usedLegacy) kvRemove(LEGACY_PERSISTENT_NOTIFICATIONS_KEY);
      return { notifications: [], usedLegacy, key: scopedKey, corrupt: true };
    }
    try {
      parsed = JSON.parse(stored);
    } catch {
      kvRemove(scopedKey);
      if (usedLegacy) kvRemove(LEGACY_PERSISTENT_NOTIFICATIONS_KEY);
      return { notifications: [], usedLegacy, key: scopedKey, corrupt: true };
    }
  } else if (Array.isArray(stored)) {
    parsed = stored;
  } else {
    kvRemove(scopedKey);
    return { notifications: [], usedLegacy, key: scopedKey, corrupt: true };
  }

  // Sanitize without retagging, then keep only rows already scoped to this business
  const cleaned = sanitizeNotificationList(parsed, {});
  const notifications = businessId
    ? filterNotificationsForBusiness(cleaned, businessId)
    : cleaned;

  // Migrate matching legacy rows into the scoped key once (leave foreign alerts in legacy)
  if (usedLegacy && businessId && notifications.length > 0) {
    kvSet(scopedKey, sanitizeNotificationList(notifications, { businessId }));
  }

  return { notifications, usedLegacy, key: scopedKey, corrupt: false };
}

export function savePersistentNotificationsToKv(businessId, notifications) {
  const key = persistentNotificationsStorageKey(businessId);
  const clean = sanitizeNotificationList(notifications, { businessId });
  kvSet(key, clean);
  return clean;
}

export function resolveNotificationMatches(notification, { eventKey, deviationId, id } = {}) {
  if (!notification) return false;
  if (id != null && String(notification.id) === String(id)) return true;
  if (eventKey && notification.data?.eventKey != null) {
    if (String(notification.data.eventKey) === String(eventKey)) return true;
  }
  if (deviationId != null && notification.data?.deviationId != null) {
    if (String(notification.data.deviationId) === String(deviationId)) return true;
  }
  return false;
}
